/**
 * The frame rule (1.I-1 deliverable 4): the sidebar and header show only for a signed-in user
 * with the `shell` flag on, and never on the login page or the pages that wear their own token
 * roots (the design page, 1.H's capture page, the gate pages).
 */
import { type Flags, flagsFromMe, type MeResponse } from '@codex/contracts';

export const FRAMELESS_PREFIXES = ['/design', '/capture-clipboard', '/gate'] as const;

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** True for a path that renders without the frame and without the Noir token root. */
export function isFramelessPath(pathname: string): boolean {
  return FRAMELESS_PREFIXES.some((prefix) => under(pathname, prefix));
}

/**
 * The flags of the remembered user, read where a throw would take down the root tree (the frame
 * rule, the open modes, the palette). A malformed `auth.me` answer fails closed: null, so the
 * shell is off and the page's own guard decides.
 */
export function safeFlags(me: MeResponse | null): Flags | null {
  if (me === null) return null;
  try {
    return flagsFromMe(me);
  } catch {
    return null;
  }
}

export function showFrame(args: { me: MeResponse | null; pathname: string }): boolean {
  if (safeFlags(args.me)?.shell !== true) return false;
  if (under(args.pathname, '/login')) return false;
  return !isFramelessPath(args.pathname);
}
